import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Users, Check, Link2, Unlink, Search, Shield } from "lucide-react";
import { useLanguage } from "../../LanguageProvider";
import { useAuth } from "../../AuthProvider";
import toast from "react-hot-toast";
import swishDark from "../../assets/Swish Logo Primary Dark-BG SVG.svg";
import swishLight from "../../assets/Swish Logo Primary Light-BG SVG.svg";
import "../../styles/components/profile-modal.css";

export const ProfileModal = ({ isOpen, onClose, allParticipants = [] }) => {
  const { t } = useLanguage();
  const {
    userProfile,
    swishNumber,
    linkedParticipantId,
    updateSwishNumber,
    linkParticipant,
    unlinkParticipant,
    refreshUserProfile,
  } = useAuth();

  const [localSwish, setLocalSwish] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLinking, setIsLinking] = useState(false);

  // Sync local state from auth context when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalSwish(swishNumber || "");
      setSearchTerm("");
    }
  }, [isOpen, swishNumber]);

  const linkedParticipant = useMemo(() => {
    return allParticipants.find((p) => p.id === linkedParticipantId);
  }, [allParticipants, linkedParticipantId]);

  const availableParticipants = useMemo(() => {
    const filtered = allParticipants.filter(
      (p) => !p.linked_user_id || p.id === linkedParticipantId
    );
    if (!searchTerm) return filtered;
    return filtered.filter((p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allParticipants, searchTerm, linkedParticipantId]);

  const handleSave = async () => {
    setIsSaving(true);
    const { error } = await updateSwishNumber(localSwish);
    if (!error) {
      toast.success(t("profile_saved"));
      onClose();
    } else {
      toast.error(t("error_occurred"));
    }
    setIsSaving(false);
  };

  const handleLink = async (participantId) => {
    setIsLinking(true);
    const { error } = await linkParticipant(participantId);
    if (!error) {
      toast.success(t("profile_saved"));
      refreshUserProfile();
    } else {
      toast.error(t("error_occurred"));
    }
    setIsLinking(false);
  };

  const handleUnlink = async () => {
    setIsLinking(true);
    const { error } = await unlinkParticipant();
    if (!error) {
      toast.success(t("profile_saved"));
      refreshUserProfile();
    } else {
      toast.error(t("error_occurred"));
    }
    setIsLinking(false);
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="modal-overlay-wrapper centered">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-backdrop"
            onClick={onClose}
          />
          <div className="modal-container">
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="confirm-modal profile-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close-btn" onClick={onClose}>
                <X size={20} />
              </button>

              {/* Header */}
              <div className="profile-modal-header">
                <div className="profile-modal-avatar-section">
                  <div className={`profile-modal-avatar-wrapper ${linkedParticipantId ? 'avatar-ring-verified' : ''}`}>
                    {userProfile?.avatarUrl ? (
                      <img
                        src={userProfile.avatarUrl}
                        alt={userProfile.name}
                        className="profile-modal-avatar"
                      />
                    ) : (
                      <div className="profile-modal-avatar-fallback">
                        {userProfile?.name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                    )}
                    {linkedParticipantId && <span className="verified-dot"><Check size={7} /></span>}
                  </div>
                  <div className="profile-modal-user-info">
                    <span className="profile-modal-name">{userProfile?.name}</span>
                    <span className="profile-modal-email">{userProfile?.email}</span>
                  </div>
                </div>
                <h2 className="profile-modal-title">{t("profile_settings")}</h2>
              </div>

              {/* Scrollable Body */}
              <div className="profile-modal-body">
                {/* Swish Section */}
                <div className="profile-section">
                  <div className="profile-section-header">
                    <div className="profile-section-icon swish-auth-icon">
                      <img src={swishLight} className="swish-logo-img light-mode-logo" alt="Swish" />
                      <img src={swishDark} className="swish-logo-img dark-mode-logo" alt="Swish" />
                    </div>
                    <div className="profile-section-label">
                      <span className="profile-section-title">{t("your_swish_number")}</span>
                      <span className="profile-section-hint">{t("swish_number_hint")}</span>
                    </div>
                  </div>
                  <div className="profile-input-wrapper">
                    <input
                      type="tel"
                      placeholder="07x xxx xxxx"
                      value={localSwish}
                      onChange={(e) => setLocalSwish(e.target.value)}
                      className="profile-input"
                    />
                  </div>
                </div>

                {/* Linked Participant Section */}
                <div className="profile-section">
                  <div className="profile-section-header">
                    <div className="profile-section-icon link">
                      <Link2 size={18} />
                    </div>
                    <div className="profile-section-label">
                      <span className="profile-section-title">{t("linked_participant")}</span>
                      <span className="profile-section-hint">{t("link_participant_hint")}</span>
                    </div>
                  </div>

                  {linkedParticipant ? (
                    <div className="linked-participant-card">
                      <div className="linked-participant-info">
                        <div className="linked-participant-avatar avatar-ring-verified">
                          {linkedParticipant.profile_image_url ? (
                            <img
                              src={linkedParticipant.profile_image_url}
                              alt={linkedParticipant.name}
                            />
                          ) : (
                            <span>{linkedParticipant.name.charAt(0).toUpperCase()}</span>
                          )}
                          <span className="verified-dot"><Check size={7} /></span>
                        </div>
                        <div className="linked-participant-meta">
                          <span className="linked-participant-name">
                            {linkedParticipant.name}
                          </span>
                          <span className="linked-participant-badge">
                            <Shield size={12} />
                            {t("verified_player")}
                          </span>
                        </div>
                      </div>
                      <button
                        className="btn-unlink"
                        onClick={handleUnlink}
                        disabled={isLinking}
                      >
                        <Unlink size={14} />
                        {t("unlink")}
                      </button>
                    </div>
                  ) : (
                    <div className="participant-link-selector">
                      <div className="link-search-bar">
                        <Search size={16} className="link-search-icon" />
                        <input
                          type="text"
                          placeholder={t("select_participant_to_link")}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="link-search-input"
                        />
                      </div>
                      <div className="link-participant-list">
                        {availableParticipants.length > 0 ? (
                          availableParticipants.map((p) => (
                            <button
                              key={p.id}
                              className={`link-participant-item ${linkedParticipantId === p.id ? "active" : ""}`}
                              onClick={() => handleLink(p.id)}
                              disabled={isLinking}
                            >
                              <div className="link-item-avatar">
                                {p.profile_image_url ? (
                                  <img src={p.profile_image_url} alt={p.name} />
                                ) : (
                                  <span>{p.name.charAt(0).toUpperCase()}</span>
                                )}
                              </div>
                              <span className="link-item-name">{p.name}</span>
                              {linkedParticipantId === p.id && (
                                <Check size={16} className="link-item-check" />
                              )}
                            </button>
                          ))
                        ) : (
                          <div className="link-empty-state">
                            <Users size={24} />
                            <span>{t("no_participant_linked")}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="profile-modal-footer">
                <button className="modal-btn-secondary" onClick={onClose}>
                  {t("cancel")}
                </button>
                <button
                  className="modal-btn-primary info"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? t("loading") : t("save")}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};
